import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { FormEventHandler, MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Control, FieldValues, UseFormSetValue, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useGetLoginUserInfo } from '../../../../api/auth/auth';
import {
  GetConfirmationRequestAttachedFileQueryResult,
  useGetConfirmationRequestAttachedFile,
  useGetConfirmationRequestDetail,
  usePostConfirmationRequsestAdd,
  usePostConfirmationRequsestUpdate,
} from '../../../../api/confirmation-requests/confirmation-requests';
import {
  AttachedFile,
  ConfirmationRequestAdd,
  ConfirmationRequestDetail,
  ConfirmationRequestUpdate,
  ProductionMasters,
  TreeCompany,
} from '../../../../api/model';
import POST_MESSAGES from '../../../../constants/postMessages';
import { useDefaultOptions, useOptionsForBinary } from '../../../../hooks/api';
import usePostMessage from '../../../../hooks/usePostMessage';
import yup from '../../../../utils/custom-yup';
import ErrorComponent from '../../../atoms/ErrorComponent';
import FileDrop, { FileWithId } from '../../../atoms/FileDrop';
import SingleSelect from '../../../atoms/SingleSelect';
import WaitComponent from '../../../atoms/WaitComponent';
import ErrorModal from '../../../modules/ErrorModal';
import OkModal from '../../../modules/OkModal';
import { getOptions } from '../../../modules/OrganizationTreeMultiSelect';
import SpinnerModal from '../../../modules/SpinnerModal';
import AttachedFileDownloader from './AttachedFileDownloader';
import { DatePickerGroup, InputGroup, OrganizationTreeMultiSelectGroup } from './FormElements';
import ProductionConfirmationRequestEditAddFile from './ProductionConfirmationRequestEditAddFile';
import { ConfirmationRequestCategories, ConfirmationRequestStatuses } from './ProductionConfirmationRequestsTab';

// 操作(1:保存 2:確認依頼 3:回答 4:転送 5:完了)
const ConfirmationRequestActions = {
  save: 1,
  request: 2,
  reply: 3,
  forward: 4,
  complete: 5,
} as { [name: string]: number };

interface ProductionConfirmationRequestAddProps {
  productionId: number;
  confirmationRequestDivisionId: number;
  masters: ProductionMasters;
  companyTrees: TreeCompany[];
}

/**
 * 確認依頼タブの確認依頼追加画面
 * @param param0
 * @returns
 */
function ProductionConfirmationRequestAdd({
  productionId,
  confirmationRequestDivisionId,
  masters,
  companyTrees,
}: ProductionConfirmationRequestAddProps) {
  const refetchDetail = () => 1; // 空関数
  return (
    <ProductionConfirmationRequestMain
      productionId={productionId}
      confirmationRequestId={undefined}
      confirmationRequestDivisionId={confirmationRequestDivisionId}
      confirmationRequestDetail={undefined}
      isReadonly={false}
      masters={masters}
      companyTrees={companyTrees}
      refetchDetail={refetchDetail}
    />
  );
}

interface ProductionConfirmationRequestEditProps {
  productionId: number;
  confirmationRequestId: number;
  isReadonly: boolean;
  masters: ProductionMasters;
  companyTrees: TreeCompany[];
}

/**
 * 確認依頼タブの確認依頼編集画面
 * @param param0
 * @returns
 */
function ProductionConfirmationRequestEdit({
  productionId,
  confirmationRequestId,
  isReadonly,
  masters,
  companyTrees,
}: ProductionConfirmationRequestEditProps) {
  const {
    i18n: { language },
  } = useTranslation();

  const options = useDefaultOptions(language);
  const {
    data: confirmationRequestDetailResult,
    isError,
    error,
    refetch: refetchDetail,
  } = useGetConfirmationRequestDetail(confirmationRequestId, options);
  if (isError) {
    return <ErrorComponent error={error} />;
  }

  const confirmationRequestDetail = confirmationRequestDetailResult?.data;
  return confirmationRequestDetail != null ? (
    <ProductionConfirmationRequestMain
      productionId={productionId}
      confirmationRequestId={confirmationRequestId}
      confirmationRequestDivisionId={undefined}
      confirmationRequestDetail={confirmationRequestDetail}
      isReadonly={isReadonly}
      masters={masters}
      companyTrees={companyTrees}
      refetchDetail={refetchDetail}
    />
  ) : (
    <WaitComponent />
  );
}

export { ProductionConfirmationRequestAdd, ProductionConfirmationRequestEdit };

const OrganizationIdArrayToCsv = (ids: string[], type: string): string =>
  ids
    ?.filter((x) => x.indexOf(type) !== -1)
    ?.map((x) => x.replace(`${type}_`, ''))
    .join(',');

interface ProductionConfirmationRequestMainProps {
  productionId: number;
  confirmationRequestId: number | undefined;
  confirmationRequestDivisionId: number | undefined;
  confirmationRequestDetail: ConfirmationRequestDetail | undefined;
  isReadonly: boolean;
  masters: ProductionMasters;
  companyTrees: TreeCompany[];
  refetchDetail: () => void;
}

/**
 * 確認依頼編集画面のメインエリアコンポーネント
 * @param param0
 * @returns
 */
function ProductionConfirmationRequestMain({
  productionId,
  confirmationRequestId,
  confirmationRequestDivisionId,
  confirmationRequestDetail,
  isReadonly,
  companyTrees,
  refetchDetail,
}: ProductionConfirmationRequestMainProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // Form定義
  const selectedActionRef = useRef<number>();

  const schema = yup.object({
    title: yup
      .string()
      .nullable()
      .transform((v) => (v ? (v as string) : undefined)),
    url: yup
      .string()
      .nullable()
      .transform((v) => (v ? (v as string) : undefined)),
    judge: yup
      .string()
      .nullable()
      .transform((v) => (v ? (v as string) : undefined)),
    due_on: yup.lazy(() => {
      if (selectedActionRef.current === ConfirmationRequestActions.request) {
        return yup
          .string()
          .required()
          .date()
          .nullable()
          .transform((v) => (v ? (v as string) : undefined));
      }
      return yup
        .string()
        .date()
        .nullable()
        .transform((v) => (v ? (v as string) : undefined));
    }),
    comment: yup
      .string()
      .nullable()
      .transform((v) => (v ? (v as string) : undefined)),
    confirmation_user_ids: yup.lazy(() => {
      if (selectedActionRef.current === ConfirmationRequestActions.forward) {
        return yup
          .array()
          .min(1)
          .max(1)
          .transform((v) => (v ? (v as string[]) : undefined));
      }
      if (selectedActionRef.current === ConfirmationRequestActions.request) {
        return yup
          .array()
          .min(1)
          .transform((v) => (v ? (v as string[]) : undefined));
      }
      return yup
        .array()
        .nullable()
        .transform((v) => (v ? (v as string[]) : undefined));
    }),
  });

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      ...confirmationRequestDetail,
      confirmation_user_ids: (
        confirmationRequestDetail?.confirmation_users
          ?.filter((x) => x.user_id != null)
          .map((x) => `user_${x.user_id}`) ?? []
      ).concat(
        confirmationRequestDetail?.confirmation_teams
          ?.filter((x) => x.team_id != null)
          .map((x) => `team_${x.team_id}`) ?? []
      ),
      confirmationRequestFiles: [] as FileWithId[],
    },
    resolver: yupResolver(schema),
  });

  const watchConfirmationUserIds = watch('confirmation_user_ids');

  useEffect(() => {
    selectedActionRef.current = undefined;
  }, [watchConfirmationUserIds]);

  // 宛先一覧取得
  const organizationOptions = useMemo(
    () => getOptions(companyTrees ?? [], language === 'ja'),
    [companyTrees, language]
  );

  // 更新処理定義
  const options = useDefaultOptions(language);

  const { mutateAsync: confirmationRequsestUpdateAsync, error: confirmationRequsestUpdateError } =
    usePostConfirmationRequsestUpdate(options);

  const [displayConfirmationRequsestUpdateError, setDisplayConfirmationRequsestUpdateError] = useState(false);

  useEffect(
    () => setDisplayConfirmationRequsestUpdateError(confirmationRequsestUpdateError != null),
    [confirmationRequsestUpdateError]
  );

  // 追加処理定義
  const { mutateAsync: confirmationRequsestAddAsync, error: confirmationRequsestAddError } =
    usePostConfirmationRequsestAdd(options);

  const [displayConfirmationRequsestAddError, setDisplayConfirmationRequsestAddError] = useState(false);

  useEffect(
    () => setDisplayConfirmationRequsestAddError(confirmationRequsestAddError != null),
    [confirmationRequsestAddError]
  );

  // リビジョン保存チェック状態
  const [saveRevision, setSaveRevision] = useState<boolean>(true);

  // 追加，更新処理
  const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);
  const [displayCannotFowardError, setDisplayCannotFowardError] = useState(false);

  const { postMessage } = usePostMessage(POST_MESSAGES.ReloadConfirmationRequestList);

  const [params] = useSearchParams();
  const navigate = useNavigate();

  // キャンセル
  const onCancelClicked: MouseEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      e.preventDefault();

      if (params.get('mode') === 'home') {
        navigate(`/${language}/productions/edit/${productionId}`);
      } else {
        window.close();
      }
    },
    [navigate, language, productionId, params]
  );

  // 遷移状態フラグ生成
  const loginUserInfoResponse = useGetLoginUserInfo(options);
  const loginUser = loginUserInfoResponse.data?.data;

  const { isAdd, isNewRepliedForwardedCompleted, isReplied, isRequested, isUpdatable, isSystemNotification } = useMemo(
    () => ({
      isAdd: !confirmationRequestId,
      isNewRepliedForwardedCompleted:
        confirmationRequestDetail?.confirmation_request_status === ConfirmationRequestStatuses.New ||
        confirmationRequestDetail?.confirmation_request_status === ConfirmationRequestStatuses.Replied ||
        confirmationRequestDetail?.confirmation_request_status === ConfirmationRequestStatuses.Forwarded ||
        confirmationRequestDetail?.confirmation_request_status === ConfirmationRequestStatuses.Completed,
      isReplied: confirmationRequestDetail?.confirmation_request_status === ConfirmationRequestStatuses.Replied,
      isRequested: confirmationRequestDetail?.confirmation_request_status === ConfirmationRequestStatuses.Requested,
      isUpdatable: confirmationRequestDetail?.updatable,
      isSystemNotification:
        confirmationRequestDetail?.confirmation_request_category_id === ConfirmationRequestCategories.System &&
        (confirmationRequestDetail?.request_user_id === loginUser?.user_id ||
          confirmationRequestDetail?.request_user_id == null),
    }),
    [
      confirmationRequestId,
      confirmationRequestDetail?.confirmation_request_status,
      confirmationRequestDetail?.updatable,
      confirmationRequestDetail?.confirmation_request_category_id,
      confirmationRequestDetail?.request_user_id,
      loginUser?.user_id,
    ]
  );

  // 原稿URL ベースパス
  const basePath = window.location.origin + (process.env.REACT_APP_ROUTER_BASEPATH || '');

  const { dispSaveRevision } = useMemo(
    () => ({
      dispSaveRevision:
        !isReadonly &&
        isUpdatable &&
        confirmationRequestDetail?.url !== null &&
        (confirmationRequestDetail?.confirmation_request_division_id === 2 ||
          confirmationRequestDetail?.confirmation_request_division_id === 6 ||
          confirmationRequestDetail?.confirmation_request_division_id === 13 ||
          confirmationRequestDetail?.confirmation_request_division_id === 22 ||
          confirmationRequestDetail?.confirmation_request_division_id === 24 ||
          confirmationRequestDetail?.confirmation_request_division_id === 25),
    }),
    [isReadonly, isUpdatable, confirmationRequestDetail]
  );

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (e) => {
      const upsertConfirmationRequest = async (confirmationRequestAction: number) => {
        await handleSubmit(async (data) => {
          if (confirmationRequestId) {
            await confirmationRequsestUpdateAsync({
              confirmationRequestId,
              data: {
                ...data,
                action: confirmationRequestAction,
                confirmation_user_ids: OrganizationIdArrayToCsv(data.confirmation_user_ids, 'user'),
                confirmation_team_ids: OrganizationIdArrayToCsv(data.confirmation_user_ids, 'team'),
                'files[]': data.confirmationRequestFiles.filter((x) => !x.id),
                file_ids: data.confirmationRequestFiles
                  .filter((x) => x.id != null)
                  .map((x) => x.id)
                  .join(','),
                base_history_id: undefined,
                save_revision: saveRevision && dispSaveRevision,
              } as ConfirmationRequestUpdate,
            });
          } else {
            await confirmationRequsestAddAsync({
              productionId,
              data: {
                ...data,
                action: confirmationRequestAction,
                confirmation_request_division_id: confirmationRequestDivisionId,
                confirmation_user_ids: OrganizationIdArrayToCsv(data.confirmation_user_ids, 'user'),
                confirmation_team_ids: OrganizationIdArrayToCsv(data.confirmation_user_ids, 'team'),
                'files[]': data.confirmationRequestFiles.filter((x) => !x.id),
                file_ids: data.confirmationRequestFiles
                  .filter((x) => x.id != null)
                  .map((x) => x.id)
                  .join(','),
              } as ConfirmationRequestAdd,
            });
          }

          setDisplaySpinnerModal(false);
          postMessage();
          window.close();
        })();
      };

      e.preventDefault();
      setDisplaySpinnerModal(true);

      const submitterId: string = e.nativeEvent instanceof SubmitEvent ? e.nativeEvent.submitter?.id ?? '' : '';

      if (submitterId in ConfirmationRequestActions) {
        selectedActionRef.current = ConfirmationRequestActions[submitterId];
        await upsertConfirmationRequest(ConfirmationRequestActions[submitterId]);
      }

      setDisplaySpinnerModal(false);
    },
    [
      setDisplaySpinnerModal,
      confirmationRequestId,
      postMessage,
      handleSubmit,
      productionId,
      confirmationRequestDivisionId,
      confirmationRequsestAddAsync,
      confirmationRequsestUpdateAsync,
      saveRevision,
      dispSaveRevision,
    ]
  );

  return (
    <>
      <form onSubmit={onSubmit}>
        <fieldset disabled={displaySpinnerModal || isReadonly} className="fieldset-reset">
          <div className="approval">
            <div className="approval1">
              <ul>
                <li>
                  <label>{t('大分類')}</label>
                  <span>{confirmationRequestDetail?.confirmation_request_category_name}</span>
                </li>
                <li>
                  <label>{t('分類')}</label>
                  <span>{confirmationRequestDetail?.confirmation_request_division_name}</span>
                </li>
                <li>
                  <label>{t('記載章')}</label>
                  <span>{confirmationRequestDetail?.confirmation_request_chapter_name}</span>
                </li>
              </ul>
            </div>
            <div className="approval2">
              <ul>
                <li>
                  <label>{t('Gate確認用')}</label>
                  <span>
                    <span className="avttl">{t('判定')}</span>
                    {isAdd || isUpdatable ? (
                      <SingleSelect
                        id="judge"
                        name="judge"
                        allowEmpty
                        options={[t('再'), t('OK')]}
                        register={register}
                        getValue={(x) => x}
                        getLabel={(x) => x}
                      />
                    ) : (
                      confirmationRequestDetail?.judge
                    )}
                  </span>
                </li>
                <li>
                  <label>{t('ステータス')}</label>
                  <span>
                    <span className="avttl">{confirmationRequestDetail?.production_status_name}</span>
                  </span>
                </li>
                <li>
                  <label>{t('ステータス終了日')}</label>
                  <span>{confirmationRequestDetail?.production_status_end_date}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="split2">
            <div className="inputbox1 inputbox">
              <ul>
                {isAdd || isUpdatable ? (
                  <InputGroup label={t('件名')} name="title" errors={errors} register={register} />
                ) : (
                  <li>
                    <label>{t('件名')}</label>
                    <span>{confirmationRequestDetail?.title}</span>
                  </li>
                )}
                {(isAdd || isUpdatable) && !isRequested ? (
                  <OrganizationTreeMultiSelectGroup
                    label={t('宛先')}
                    name="confirmation_user_ids"
                    control={control}
                    options={organizationOptions}
                    selectableTypes={['user', 'team']}
                    isDisabled={displaySpinnerModal}
                    errors={errors}
                  />
                ) : (
                  <li>
                    <label>{t('宛先')}</label>
                    <span>
                      {(confirmationRequestDetail?.confirmation_users?.map((user) => user.user_name) ?? [])
                        .concat(confirmationRequestDetail?.confirmation_teams?.map((team) => team.team_name) ?? [])
                        .join(', ')}
                    </span>
                  </li>
                )}
                {confirmationRequestDetail?.url != null ? (
                  <li>
                    <label htmlFor="confreq3">{t('原稿URL')}</label>
                    <Link
                      style={{ paddingTop: '5px' }}
                      to={`/${language}/productions/sections/${productionId}/ps?section_id=${confirmationRequestDetail?.url}`}
                      target="_blank"
                    >
                      {`${basePath}/${language}/productions/sections/${productionId}/ps?section_id=${confirmationRequestDetail?.url}`}
                    </Link>
                  </li>
                ) : (
                  <li>
                    <label htmlFor="confreq3">{t('原稿URL')}</label>
                  </li>
                )}
              </ul>
            </div>
            <div className="inputbox2 inputbox">
              <ul>
                {(isAdd || isUpdatable) && !isRequested ? (
                  <DatePickerGroup
                    label={t('回答期限')}
                    name="due_on"
                    isDisabled={isReadonly}
                    control={control as unknown as Control<FieldValues>}
                    errors={errors}
                  />
                ) : (
                  <li>
                    <label>{t('回答期限')}</label>
                    <span>{confirmationRequestDetail?.due_on}</span>
                  </li>
                )}
              </ul>
              <div className="confreqtxt">
                <p>
                  {t('締切日までの日数を考慮し設定ください。')}
                  <br />
                  {t('G1、G3、G4：10日確保しているときは、1回目(HM)2日→回答確認2日(原)→以降は1日か当日。')}
                  <br />
                  {t('G2：15日確保しているときは、1回目(HM)5日→回答確認2日(原)→以降は2日')}
                </p>
              </div>
            </div>
          </div>

          <div>
            <div className="threadfr2 mt30 mb20">
              <label htmlFor="">{t('スレッド')}</label>
              <div>
                <ul>
                  {confirmationRequestDetail?.confirmation_request_histories?.map((history) => (
                    <li key={history.confirmation_request_history_id}>
                      <div style={{ display: 'flex', gap: '30px' }}>
                        <div style={{ flexBasis: '50%' }}>
                          <span className="user-date">
                            <span className="t-user">{history.update_user_name}</span>
                            <span className="t-date">
                              <span>
                                {t('送信日：')}
                                {history.updated_at}
                              </span>
                              <span>
                                {t('Gate確認用：')}
                                {history.judge}
                              </span>
                              <span>
                                {t('回答期限：')}
                                {history.due_on}
                              </span>
                            </span>
                          </span>
                          <p className="t-comment">{history.comment}</p>
                          <span className="drawingfile">
                            {history.attached_files?.map((file) => (
                              <ConfirmationRequestAttachedFile
                                attachedFile={file}
                                key={file.file_id}
                                refetchDetail={refetchDetail}
                              />
                            ))}
                          </span>
                        </div>
                        <ProductionConfirmationRequestEditAddFile
                          id={`confirmationRequestFiles${history.confirmation_request_history_id}`}
                          name={`confirmationRequestFiles${history.confirmation_request_history_id}`}
                          setValue={setValue as unknown as UseFormSetValue<FieldValues>}
                          selectedFiles={confirmationRequestDetail?.attached_files?.map(
                            (x) => ({ id: x.file_id, name: x.file_name } as FileWithId)
                          )}
                          historyId={history.confirmation_request_history_id}
                          refetchDetail={refetchDetail}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          {(isAdd || isUpdatable) && (
            <>
              <div className="formarea1 mt5 fa1_confreq">
                <label htmlFor="">{t('コメント')}</label>
                <textarea id="comment" rows={10} cols={33} {...register('comment')} />
                {errors.comment?.message != null && <p className="form-error">{errors.comment.message.toString()}</p>}
              </div>

              <div className="formarea1 mt10 fa1_confreq">
                <label htmlFor="">{t('添付ファイル')}</label>
                <FileDrop
                  id="confirmationRequestFiles"
                  name="confirmationRequestFiles"
                  setValue={setValue as unknown as UseFormSetValue<FieldValues>}
                  selectedFiles={confirmationRequestDetail?.attached_files?.map(
                    (x) => ({ id: x.file_id, name: x.file_name } as FileWithId)
                  )}
                />
              </div>
            </>
          )}
        </fieldset>

        <fieldset disabled={displaySpinnerModal} className="fieldset-reset">
          <div className="btnfr-all mt20 bfall1 conf-req-btn1fr">
            <div className="btnfr-left3">
              <div className="btnfr leftfr">
                <div>
                  {!isReadonly && isUpdatable && isNewRepliedForwardedCompleted && (
                    <span>
                      <input
                        type="submit"
                        id="forward"
                        name="forward"
                        value={t('転送').toString()}
                        className="btn1 lsp1"
                      />
                    </span>
                  )}
                  {!isReadonly && isUpdatable && (isReplied || isSystemNotification) && (
                    <span>
                      <input
                        type="submit"
                        id="complete"
                        name="complete"
                        value={t('完了').toString()}
                        className="btn1 lsp1"
                      />
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="btnfr-right3">
              <div className="btnfr btnfr4">
                <div>
                  {/* 小区分が G1, G2, G3, G4, 校了, 校了後修正 の時のみ表示 */}
                  {!isReadonly &&
                    isUpdatable &&
                    confirmationRequestDetail?.url !== null &&
                    (confirmationRequestDetail?.confirmation_request_division_id === 2 ||
                      confirmationRequestDetail?.confirmation_request_division_id === 6 ||
                      confirmationRequestDetail?.confirmation_request_division_id === 13 ||
                      confirmationRequestDetail?.confirmation_request_division_id === 22 ||
                      confirmationRequestDetail?.confirmation_request_division_id === 24 ||
                      confirmationRequestDetail?.confirmation_request_division_id === 25) && (
                      <span className="ck">
                        <input
                          id="save_revision"
                          type="checkbox"
                          checked={saveRevision}
                          onChange={(e) => {
                            setSaveRevision(e.target.checked);
                          }}
                        />{' '}
                        <label htmlFor="save_revision" style={{ fontSize: '15px', fontWeight: 'normal' }}>
                          {t('改訂履歴保存')}
                        </label>
                      </span>
                    )}
                  <span className="btn4 btnbg-g cursor">
                    <input
                      type="submit"
                      id="cancel"
                      name="cancel"
                      value={t('キャンセル').toString()}
                      className="btn1 btnbg-g link-b8"
                      onClick={(e) => onCancelClicked(e)}
                    />
                  </span>
                  {!isReadonly && (isAdd || isUpdatable) && (
                    <span>
                      <input type="submit" id="save" name="save" value={t('保存').toString()} className="btn1 lsp1" />
                    </span>
                  )}
                  {!isReadonly && isUpdatable && isRequested && (
                    <span>
                      <input type="submit" id="reply" name="reply" value={t('回答').toString()} className="btn1 lsp1" />
                    </span>
                  )}
                  {!isReadonly && (isAdd || (isUpdatable && isNewRepliedForwardedCompleted)) && (
                    <span>
                      <input
                        type="submit"
                        id="request"
                        name="request"
                        value={t('確認依頼').toString()}
                        className="btn1"
                      />
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </fieldset>
      </form>
      {displaySpinnerModal && <SpinnerModal />}
      {displayConfirmationRequsestUpdateError && (
        <ErrorModal
          error={confirmationRequsestUpdateError}
          onClickOk={() => {
            setDisplaySpinnerModal(false);
            setDisplayConfirmationRequsestUpdateError(false);
          }}
        />
      )}
      {displayConfirmationRequsestAddError && (
        <ErrorModal
          error={confirmationRequsestAddError}
          onClickOk={() => {
            setDisplaySpinnerModal(false);
            setDisplayConfirmationRequsestAddError(false);
          }}
        />
      )}
      {displayCannotFowardError && (
        <OkModal onClickOk={() => setDisplayCannotFowardError(false)}>
          <div>{t('複数の宛先への転送はできません。')}</div>
        </OkModal>
      )}
    </>
  );
}

interface ConfirmationRequestAttachedFileProps {
  attachedFile: AttachedFile;
  refetchDetail: () => void;
}

/**
 * 添付ファイル表示，取得用コンポーネント
 * @param param0
 * @returns
 */
function ConfirmationRequestAttachedFile({ attachedFile, refetchDetail }: ConfirmationRequestAttachedFileProps) {
  const {
    i18n: { language },
  } = useTranslation();

  const options = useOptionsForBinary(language);

  const { refetch: refetchConfirmationRequestAttachedFile, error: getConfirmationRequestAttachedFileError } =
    useGetConfirmationRequestAttachedFile<GetConfirmationRequestAttachedFileQueryResult, AxiosError<Blob>>(
      attachedFile.file_id,
      options
    );

  return (
    <AttachedFileDownloader
      attachedFile={attachedFile}
      refetch={refetchConfirmationRequestAttachedFile}
      error={getConfirmationRequestAttachedFileError}
      mode="Confirmation"
      refetchDetail={refetchDetail}
    />
  );
}
